/**
 * Utility functions for highlighting question text in raw text
 */

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Normalizes whitespace in a string for flexible matching
 * - Collapses multiple spaces/newlines into single space
 * - Trims the result
 */
function normalizeWhitespace(str: string): string {
  return str.replace(/\s+/g, ' ').trim()
}

/**
 * Finds the position of a question's text within raw text.
 * Handles cases where raw text has question numbers like "1. What is your name?"
 * but the question field only contains "What is your name?"
 *
 * @param questionText - The question text to find
 * @param rawText - The raw text to search in
 * @returns Object with start and end positions, or null if not found
 */
export function findQuestionInRawText(
  questionText: string,
  rawText: string,
): { start: number; end: number; matchedText: string } | null {
  if (!questionText || !rawText) return null

  const normalizedQuestion = normalizeWhitespace(questionText)
  if (!normalizedQuestion) return null

  // Try exact match first (case-insensitive)
  const exactPattern = new RegExp(escapeRegex(normalizedQuestion), 'i')
  const normalizedRaw = rawText

  // Check for exact match
  const exactMatch = normalizedRaw.match(exactPattern)
  if (exactMatch && exactMatch.index !== undefined) {
    return {
      start: exactMatch.index,
      end: exactMatch.index + exactMatch[0].length,
      matchedText: exactMatch[0],
    }
  }

  // Try matching with possible question number prefix
  // Pattern: optional number/letter prefix followed by the question text
  // e.g., "1.", "1)", "1:", "a.", "1a.", "1a)", etc.
  const prefixPattern = new RegExp(
    `(^|\\n)\\s*([0-9]+[a-z]*|[a-z])[\\.\\)\\:]?\\s*${escapeRegex(
      normalizedQuestion,
    )}`,
    'im',
  )
  const prefixMatch = rawText.match(prefixPattern)
  if (prefixMatch && prefixMatch.index !== undefined) {
    // Find where the actual question text starts within the match
    const fullMatch = prefixMatch[0]
    const questionStartInMatch = fullMatch
      .toLowerCase()
      .indexOf(normalizedQuestion.toLowerCase())
    if (questionStartInMatch !== -1) {
      const start = prefixMatch.index + questionStartInMatch
      return {
        start,
        end: start + normalizedQuestion.length,
        matchedText: rawText.slice(start, start + normalizedQuestion.length),
      }
    }
  }

  // Try fuzzy match - find first significant words
  const words = normalizedQuestion.split(' ').filter((w) => w.length > 3)
  if (words.length >= 2) {
    // Try to find first few significant words
    const firstWords = words.slice(0, 3).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+')
    const fuzzyPattern = new RegExp(firstWords, 'i')
    const fuzzyMatch = rawText.match(fuzzyPattern)
    if (fuzzyMatch && fuzzyMatch.index !== undefined) {
      // Try to find the end of this question (next blank line or end of text)
      const startPos = fuzzyMatch.index
      const restOfText = rawText.slice(startPos)
      const endMatch = restOfText.match(/\n\s*\n/)
      const endPos = endMatch
        ? startPos + endMatch.index!
        : Math.min(startPos + normalizedQuestion.length + 50, rawText.length)

      return {
        start: startPos,
        end: endPos,
        matchedText: rawText.slice(startPos, endPos),
      }
    }
  }

  return null
}

/**
 * Renders raw text with highlighted sections.
 * Returns an array of objects representing text segments.
 *
 * @param rawText - The raw text to render
 * @param highlightRange - The range to highlight (start, end positions)
 * @returns Array of segments with text and isHighlighted flag
 */
export function getHighlightedSegments(
  rawText: string,
  highlightRange: { start: number; end: number } | null,
): Array<{ text: string; isHighlighted: boolean }> {
  if (!highlightRange) {
    return [{ text: rawText, isHighlighted: false }]
  }

  const { start, end } = highlightRange
  const segments: Array<{ text: string; isHighlighted: boolean }> = []

  if (start > 0) {
    segments.push({ text: rawText.slice(0, start), isHighlighted: false })
  }

  segments.push({ text: rawText.slice(start, end), isHighlighted: true })

  if (end < rawText.length) {
    segments.push({ text: rawText.slice(end), isHighlighted: false })
  }

  return segments
}

/**
 * Calculates approximate scroll position for a textarea based on character position.
 * This is an estimate since we can't know exact pixel position without rendering.
 *
 * @param textarea - The textarea element
 * @param charPosition - The character position to scroll to
 * @returns The approximate scrollTop value
 */
export function calculateTextareaScrollPosition(
  textarea: HTMLTextAreaElement,
  charPosition: number,
): number {
  const text = textarea.value.slice(0, charPosition)
  const lines = text.split('\n').length - 1

  // Estimate line height (usually around 20px for default textarea)
  const computedStyle = window.getComputedStyle(textarea)
  const lineHeight = parseFloat(computedStyle.lineHeight) || 20
  const paddingTop = parseFloat(computedStyle.paddingTop) || 0

  // Calculate approximate scroll position, centering the target in view
  const targetScroll = lines * lineHeight
  const viewportHeight = textarea.clientHeight
  const centeredScroll = Math.max(0, targetScroll - viewportHeight / 3)

  return centeredScroll
}
